import { type BottleUnit, formatAmount, type TrackableType, type Tracked } from "@babia/checks";
import { entryDetail } from "../../lib/entry-text.ts";
import type { Glance } from "../../lib/overview.ts";
import type { Baby } from "../../lib/queries.ts";
import { ago, agoText, clock, minutesText, pluralize } from "../../lib/time.ts";
import { cn, perRow } from "../../lib/utils.ts";

/** Babies to a table: four columns still fit a 320px phone, so more get another table. */
const MOST_COLUMNS = 4;

interface Cell {
  main: string;
  sub?: string;
}

const mins = (ms: number) => minutesText(Math.round(ms / 60_000));

/**
 * Each row of the box score: a label and how to read one baby's glance, in
 * the household's bottle unit. A row with a `type` shows only when the family
 * tracks it.
 */
const ROWS: {
  label: string;
  type?: TrackableType;
  cell: (g: Glance, now: Date, unit: BottleUnit) => Cell;
}[] = [
  {
    label: "Now",
    type: "sleep",
    cell: (g, now) =>
      g.state
        ? {
            main: `${g.state.asleep ? "Asleep" : "Awake"} ${ago(g.state.since, now)}`,
            sub: `${g.state.asleep ? "since" : "woke at"} ${clock(g.state.since)}`,
          }
        : { main: "—", sub: "No sleep logged" },
  },
  {
    label: "Last bottle",
    cell: (g, now, unit) =>
      g.lastFeed
        ? {
            main: agoText(g.lastFeed.at, now),
            sub: `${entryDetail(g.lastFeed, unit)} at ${clock(g.lastFeed.at)}`,
          }
        : { main: "—" },
  },
  {
    label: "Bottles today",
    cell: (g, _now, unit) => ({
      main: formatAmount(g.todayOz, unit),
      sub: [
        pluralize(g.todayBottles, "bottle", "bottles"),
        g.typicalOzByNow !== null ? `usually ${formatAmount(g.typicalOzByNow, unit)} by now` : null,
      ]
        .filter(Boolean)
        .join(" · "),
    }),
  },
  {
    label: "Last change",
    type: "diaper",
    cell: (g, now, unit) =>
      g.lastDiaper
        ? { main: agoText(g.lastDiaper.at, now), sub: entryDetail(g.lastDiaper, unit) }
        : { main: "—" },
  },
  {
    label: "Changes today",
    type: "diaper",
    cell: (g) => ({ main: String(g.todayChanges), sub: `${g.todayDirty} dirty` }),
  },
  {
    label: "Sleep, last 24 hr",
    type: "sleep",
    cell: (g) => ({
      main: g.sleepLast24Ms ? mins(g.sleepLast24Ms) : "—",
      sub: g.longestLast24 ? `longest ${mins(g.longestLast24.ms)}` : undefined,
    }),
  },
  {
    label: "Meds today",
    cell: (g) => (g.medsToday.length ? { main: g.medsToday.join(", ") } : { main: "—" }),
  },
];

/**
 * "Right now" as a newspaper box score: one column per baby, one row per
 * question a parent asks walking into the room. Past `MOST_COLUMNS` babies
 * it is two or three tables, one under the other, each with the same columns.
 */
export function BoxScore({
  babies,
  glances,
  now,
  tracking,
  unit,
}: {
  babies: Baby[];
  glances: Map<string, Glance>;
  now: Date;
  tracking: Tracked;
  unit: BottleUnit;
}) {
  const multi = babies.length > 1;
  const rows = ROWS.filter((row) => !row.type || tracking[row.type]);
  const columns = perRow(babies.length, MOST_COLUMNS);
  const tables: Baby[][] = [];
  for (let i = 0; i < babies.length; i += columns) tables.push(babies.slice(i, i + columns));
  return (
    <section className="mt-9" aria-label="Right now">
      <h2 className="kicker">Right now</h2>
      {tables.map((group, t) => {
        // A short last table keeps the others' columns, so every baby's lines up.
        const empty = Array.from({ length: columns - group.length }, (_, i) => `empty-${i}`);
        return (
          <table
            key={group[0]!.id}
            className={cn("w-full table-fixed border-collapse text-left", t ? "mt-6" : "mt-2")}
          >
            {multi ? (
              <thead>
                <tr className="border-b border-ink">
                  <th className="w-[28%] md:w-[22%]">
                    <span className="sr-only">Measure</span>
                  </th>
                  {group.map((b) => (
                    <th key={b.id} scope="col" className="heading truncate pb-1.5 pr-2 text-[20px]">
                      {b.name}
                    </th>
                  ))}
                  {empty.map((key) => (
                    <td key={key} />
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-t border-rule first:border-t-0">
                  <th
                    scope="row"
                    className={`py-2.5 pr-3 align-top text-[13px] font-normal opacity-65 ${multi ? "" : "w-[40%]"}`}
                  >
                    {row.label}
                  </th>
                  {group.map((b) => {
                    const g = glances.get(b.id);
                    const cell = g ? row.cell(g, now, unit) : { main: "—" };
                    return (
                      <td key={b.id} className="py-2.5 pr-2 align-top">
                        <div className="heading break-words text-[17px]">{cell.main}</div>
                        {cell.sub ? (
                          <div className="break-words text-[12px] leading-snug opacity-65">
                            {cell.sub}
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                  {empty.map((key) => (
                    <td key={key} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      })}
    </section>
  );
}
